import { defineAsyncComponent, h, type VNode } from 'vue';
import type {
  InsideLayoutPortableText,
  PortableTextButtonRow,
  PortableTextContentGrid,
  SectionBuilderContentGrid,
  PortableTextCTABanner,
  PortableTextFAQAccordion,
  PortableTextFlipCards,
  PortableTextImage,
  SectionBuilderImage,
  PortableTextWistiaVideo,
  SectionBuilderWistiaVideo,
  SectionBuilderCTABanner,
  SectionBuilderFAQAccordion,
  PortableTextMediaTwoColumn,
  SectionBuilderMediaTwoColumn,
  SectionBuilderPortableText,
  SectionBuilderButtonRow,
  SectionBuilderFlipCards,
  CarouselCard,
  SectionBuilderLogoBand,
  SectionBuilderTestimonial,
  PortableTextTestimonial,
  SectionBuilderCalloutWithIcon,
  SectionBuilderWhatsNew,
  PortableTextCardTwoColumns,
  SectionBuilderCardTwoColumns,
  PortableTextContentCard,
  SectionBuilderContentCard,
  SectionBuilderSpeakerBio,
  PortableTextSpeakerBio,
  PortableTextCarouselCard,
  SectionBuilderCarouselCard,
  CodeEmbed,
  SectionBuilderComparisonPlanTable,
  HomepageHeader,
  SectionBuilderResourceCard,
  PortableTextForm,
  CodeEmbedPortableText,
  PersonalizationVariable,
  UssSuccessRedirect,
  SectionBuilderForm,
  SectionBuilderParallaxDeck,
  InlineImageAtom,
  InlineWistiaVideo,
  SectionBuilderAnchorMenu,
  SectionBuilderReviewCards,
  SectionBuilderJumboImageTwoColumn,
  SectionBuilderSpinningTwoColumn,
  LatestNews,
  SectionBuilderCustomTable,
  PortableTextCustomTable,
  CurrencyDropDown,
  MarketingBanners,
  BreakoutSection,
  HorizontalLine,
  ClosableBanner,
  SectionBuilderCycledTestimonial,
  HomePageInfographic,
  SectionBuilderDesignerCards,
  MarketingBanner,
  SectionBuilderHoverButtonRow,
  SectionBuilderScrollingLogoBand,
  PortableTextCalloutWithIcon,
  PortableTextCalloutBanner,
  AiAdvisor,
} from '~/sanity/types';

// Dynamic imports for true code-splitting - components only load when actually rendered
const LazyPortableTextButtonRow = defineAsyncComponent(
  () => import('~/components/Atmos/ButtonRow/PortableTextButtonRow.vue')
);
const LazySectionBuilderButtonRow = defineAsyncComponent(
  () => import('~/components/Atmos/ButtonRow/SectionBuilderButtonRow.vue')
);
const LazySectionBuilderHoverButtonRow = defineAsyncComponent(
  () => import('~/components/Atmos/HoverButtonRow/SectionBuilderHoverButtonRow.vue')
);
const LazyPortableTextCTABanner = defineAsyncComponent(
  () => import('~/components/Organisms/CTABanner/PortableTextCTABanner.vue')
);
const LazySectionBuilderCTABanner = defineAsyncComponent(
  () => import('~/components/Organisms/CTABanner/SectionBuilderCTABanner.vue')
);
const LazyPortableTextWistiaVideo = defineAsyncComponent(
  () => import('~/components/Atmos/WistiaVideo/PortableTextWistiaVideo.vue')
);
const LazyInlineWistiaVideo = defineAsyncComponent(
  () => import('~/components/Atmos/WistiaVideo/inlineWistiaVideo.vue')
);
const LazySectionBuilderWistiaVideo = defineAsyncComponent(
  () => import('~/components/Atmos/WistiaVideo/SectionBuilderWistiaVideo.vue')
);
const LazyPortableTextImageAtom = defineAsyncComponent(
  () => import('~/components/Atmos/ImageAtom/PortableTextImageAtom.vue')
);
const LazyInlineImageAtom = defineAsyncComponent(
  () => import('~/components/Atmos/ImageAtom/inlineImageAtom.vue')
);
const LazySectionBuilderImageAtom = defineAsyncComponent(
  () => import('~/components/Atmos/ImageAtom/SectionBuilderImageAtom.vue')
);
const LazyInsideLayout = defineAsyncComponent(
  () => import('~/components/Atmos/InsideLayout/InsideLayout.vue')
);
const LazyPortableTextMediaTwoColumn = defineAsyncComponent(
  () => import('~/components/Molecules/MediaTwoColumn/PortableTextMediaTwoColumn.vue')
);
const LazySectionBuilderMediaTwoColumn = defineAsyncComponent(
  () => import('~/components/Molecules/MediaTwoColumn/SectionBuilderMediaTwoColumn.vue')
);
const LazyPortableTextContentGrid = defineAsyncComponent(
  () => import('~/components/Molecules/ContentGrid/PortableTextContentGrid.vue')
);
const LazySectionBuilderContentGrid = defineAsyncComponent(
  () => import('~/components/Molecules/ContentGrid/SectionBuilderContentGrid.vue')
);
const LazyPortableTextFAQAccordion = defineAsyncComponent(
  () => import('~/components/Atmos/Accordion/PortableTextFAQAccordion.vue')
);
const LazySectionBuilderFAQAccordion = defineAsyncComponent(
  () => import('~/components/Atmos/Accordion/SectionBuilderFAQAccordion.vue')
);
const LazyPortableTextFlipCards = defineAsyncComponent(
  () => import('~/components/Atmos/FlipCard/PortableTextFlipCards.vue')
);
const LazySectionBuilderFlipCards = defineAsyncComponent(
  () => import('~/components/Atmos/FlipCard/SectionBuilderFlipCards.vue')
);
const LazySectionBuilderTestimonial = defineAsyncComponent(
  () => import('~/components/Atmos/Testimonial/SectionBuilderTestimonial.vue')
);
const LazyPortableTextTestimonial = defineAsyncComponent(
  () => import('~/components/Atmos/Testimonial/PortableTextTestimonial.vue')
);
const LazySectionBuilderPortableText = defineAsyncComponent(
  () => import('~/components/Atmos/PortableText/SectionBuilderPortableText.vue')
);
const LazyCarouselCard = defineAsyncComponent(
  () => import('~/components/Molecules/CarouselCard/CarouselCard.vue')
);
const LazySectionBuilderLogoBand = defineAsyncComponent(
  () => import('~/components/Atmos/LogoBand/SectionBuilderLogoBand.vue')
);
const LazySectionBuilderScrollingLogoBand = defineAsyncComponent(
  () => import('~/components/Atmos/ScrollingLogoBand/SectionBuilderScrollingLogoBand.vue')
);
const LazySectionBuilderJumboImageTwoColumn = defineAsyncComponent(
  () => import('~/components/Molecules/MediaTwoColumn/SectionBuilderJumboImageTwoColumn.vue')
);
const LazySectionBuilderSpinningTwoColumn = defineAsyncComponent(
  () => import('~/components/Molecules/MediaTwoColumn/SectionBuilderSpinningTwoColumn.vue')
);
const LazySectionBuilderCalloutWithIcon = defineAsyncComponent(
  () => import('~/components/Atmos/CalloutsWithIcon/SectionBuilderCalloutWithIcon.vue')
);
const LazyPortableTextCardTwoColumns = defineAsyncComponent(
  () => import('~/components/Atmos/CardTwoColumn/PortableTextCardTwoColumns.vue')
);
const LazySectionBuilderCardTwoColumns = defineAsyncComponent(
  () => import('~/components/Atmos/CardTwoColumn/SectionBuilderCardTwoColumns.vue')
);
const LazyPortableTextContentCard = defineAsyncComponent(
  () => import('~/components/Atmos/ContentCard/PortableTextContentCard.vue')
);
const LazySectionBuilderContentCard = defineAsyncComponent(
  () => import('~/components/Atmos/ContentCard/SectionBuilderContentCard.vue')
);
const LazyPortableTextSpeakerBio = defineAsyncComponent(
  () => import('~/components/Atmos/SpeakerBio/PortableTextSpeakerBio.vue')
);
const LazySectionBuilderSpeakerBio = defineAsyncComponent(
  () => import('~/components/Atmos/SpeakerBio/SectionBuilderSpeakerBio.vue')
);
const LazySectionBuilderCarouselCard = defineAsyncComponent(
  () => import('~/components/Molecules/CarouselCard/SectionBuilderCarouselCard.vue')
);
const LazyPortableTextCarouselCard = defineAsyncComponent(
  () => import('~/components/Molecules/CarouselCard/PortableTextCarouselCard.vue')
);
// prettier-ignore
const LazySectionBuilderCycledTestimonial = defineAsyncComponent(
  () => import('~/components/HomePage/SectionBuilderCycledTestimonial/SectionBuilderCycledTestimonial.vue')
);
const LazyHomePageInfographic = defineAsyncComponent(
  () => import('~/components/HomePage/HomePageInfographic/HomePageInfographic.vue')
);
const LazySectionBuilderDesignerCards = defineAsyncComponent(
  () => import('~/components/HomePage/DesignerCardSection/SectionBuilderDesignerCards.vue')
);
const LazySectionBuilderWhatsNew = defineAsyncComponent(
  () => import('~/components/Organisms/WhatsNew/SectionBuilderWhatsNew.vue')
);
const LazyCodeEmbed = defineAsyncComponent(
  () => import('~/components/Atmos/CodeEmbed/CodeEmbed.vue')
);
const LazyInlineCodeEmbed = defineAsyncComponent(
  () => import('~/components/Atmos/CodeEmbed/inlineCodeEmbed.vue')
);
const LazySectionBuilderComparisonPlanTable = defineAsyncComponent(
  () => import('~/components/Misc/SectionBuilderComparisonPlanTable.vue')
);
const LazyHomepageHeader = defineAsyncComponent(
  () => import('~/components/Atmos/PortableText/HomepageHeader.vue')
);
const LazyCodeEmbedPortableText = defineAsyncComponent(
  () => import('~/components/Atmos/CodeEmbed/CodeEmbedPortableText.vue')
);
const LazyPortableTextForm = defineAsyncComponent(
  () => import('~/components/Atmos/Form/PortableTextForm.vue')
);
const LazyPersonalizationVariable = defineAsyncComponent(
  () => import('~/components/Atmos/PersonalizationVariable/PersonalizationVariable.vue')
);
const LazyUssSuccessRedirect = defineAsyncComponent(
  () => import('~/components/Atmos/SignUpPartials/UssSuccessRedirect/UssSuccessRedirect.vue')
);
const LazySectionBuilderForm = defineAsyncComponent(
  () => import('~/components/Atmos/Form/SectionBuilderForm.vue')
);
const LazySectionBuilderResourceCard = defineAsyncComponent(
  () => import('~/components/Molecules/ResourcesCard/SectionBuilderResourceCard.vue')
);
const LazySectionBuilderParallaxDeck = defineAsyncComponent(
  () => import('~/components/Molecules/MediaTwoColumn/SectionBuilderParallaxDeck.vue')
);
const LazySectionBuilderAnchorMenu = defineAsyncComponent(
  () => import('~/components/Atmos/AnchorMenuSections/SectionBuilderAnchorMenu.vue')
);
const LazySectionBuilderReviewCards = defineAsyncComponent(
  () => import('~/components/Molecules/ReviewCards/SectionBuilderReviewCards.vue')
);
const LazyLatestNews = defineAsyncComponent(
  () => import('~/components/Molecules/LatestNews/LatestNews.vue')
);
const LazyCurrencyDropdown = defineAsyncComponent(
  () => import('~/components/Atmos/CurrencyDropdown.vue')
);
const LazySectionBuilderCustomTable = defineAsyncComponent(
  () => import('~/components/Atmos/CustomTable/SectionBuilderCustomTable.vue')
);
const LazyPortableTextCustomTable = defineAsyncComponent(
  () => import('~/components/Atmos/CustomTable/PortableTextCustomTable.vue')
);
const LazyMarketingBanners = defineAsyncComponent(
  () => import('~/components/HomePage/MarketingBanner/MarketingBanners.vue')
);
const LazyMarketingBanner = defineAsyncComponent(
  () => import('~/components/HomePage/MarketingBanner/MarketingBanner.vue')
);
const LazyBreakoutSection = defineAsyncComponent(
  () => import('~/components/Atmos/BreakoutSection/BreakoutSection.vue')
);
const LazyHorizontalLine = defineAsyncComponent(
  () => import('~/components/Atmos/HorizontalLine/HorizontalLine.vue')
);
const LazyClosableBanner = defineAsyncComponent(
  () => import('~/components/Atmos/ClosableBanner/ClosableBanner.vue')
);
const LazyPortableTextCalloutWithIcon = defineAsyncComponent(
  () => import('~/components/Atmos/CalloutsWithIcon/PortableTextCalloutWithIcon.vue')
);
const LazyPortableTextCalloutBanner = defineAsyncComponent(
  () => import('~/components/Atmos/CalloutBanner/PortableTextCalloutBanner.vue')
);
const LazyAiAdvisor = defineAsyncComponent(
  () => import('~/components/Atmos/AiAdvisor/AiAdvisor.vue')
);

const _mappedComponentKeys = [
  'portableTextButtonRow',
  'portableTextCTABanner',
  'CTABanner',
  'portableTextWistiaVideo',
  'sectionBuilderWistiaVideo',
  'portableTextImage',
  'sectionBuilderImage',
  'portableTextFAQAccordion',
  'portableTextMediaTwoColumn',
  'sectionBuilderMediaTwoColumn',
  'insideLayoutPortableText',
  'portableTextFlipCards',
  'sectionBuilderFlipCards',
  'portableTextContentGrid',
  'sectionBuilderContentGrid',
  'callToAction',
  'sectionBuilderPortableText',
  'sectionBuilderCTABanner',
  'sectionBuilderFAQAccordion',
  'sectionBuilderButtonRow',
  'carouselCard',
  'whyOnshapeSectionBuilder',
  'sectionBuilderTestimonial',
  'sectionBuilderLogoBand',
  'portableTextTestimonial',
  'sectionBuilderCarouselCard',
  'portableTextCarouselCard',
  'codeEmbed',
  'inlineCodeEmbed',
  'inlineImageAtom',
  'inlineWistiaVideo',
  'productSectionBuilder',
  'sectionBuilderCalloutWithIcon',
  'sectionBuilderWhatsNew',
  'sectionBuilderCardTwoColumns',
  'portableTextCardTwoColumns',
  'sectionBuilderContentCard',
  'portableTextContentCard',
  'sectionBuilderSpeakerBio',
  'portableTextSpeakerBio',
  'sectionBuilderComparisonPlanTable',
  'homepageHeader',
  'portableTextForm',
  'sectionBuilderParallaxDeck',
  'sectionBuilderResourceCard',
  'ussIntent',
  'ussPlanSelection',
  'ussProPlan',
  'ussSignupMessage',
  'codeEmbedPortableText',
  'sectionBuilderForm',
  'sectionBuilderAnchorMenu',
  'ussPersona',
  'ussPersonaEduIntent',
  'ussSuccessRedirect',
  'ussLoading',
  'ussPersonaEduConverge',
  'ussMessage',
  'personalizationVariable',
  'ussEmailCheckError',
  'sectionBuilderReviewCards',
  'sectionBuilderCustomTable',
  'portableTextCustomTable',
  'sectionBuilderSpinningTwoColumn',
  'sectionBuilderJumboImageTwoColumn',
  'latestNews',
  'currencyDropDown',
  'horizontalLine',
  'breakoutSection',
  'closableBanner',
  'marketingBanners',
  'marketingBanner',
  'sectionBuilderCycledTestimonial',
  'homePageInfographic',
  'sectionBuilderDesignerCards',
  'sectionBuilderHoverButtonRow',
  'sectionBuilderScrollingLogoBand',
  'portableTextCalloutWithIcon',
  'portableTextCalloutBanner',
  'aiAdvisor',
] as const;

type MappedComponent = (typeof _mappedComponentKeys)[number];

export const componentMap = {
  portableTextButtonRow: ({ value }: { value: PortableTextButtonRow }) => {
    return h(LazyPortableTextButtonRow, { ...value });
  },
  sectionBuilderButtonRow: ({ value }: { value: SectionBuilderButtonRow }) => {
    return h(LazySectionBuilderButtonRow, { ...value });
  },
  sectionBuilderHoverButtonRow: ({ value }: { value: SectionBuilderHoverButtonRow }) => {
    return h(LazySectionBuilderHoverButtonRow, { ...value });
  },
  portableTextCTABanner: ({ value }: { value: PortableTextCTABanner }) => {
    return h(LazyPortableTextCTABanner, { ...value });
  },
  sectionBuilderCTABanner: ({ value }: { value: SectionBuilderCTABanner }) => {
    return h(LazySectionBuilderCTABanner, { ...value });
  },
  portableTextWistiaVideo: ({ value }: { value: PortableTextWistiaVideo }) => {
    return h(LazyPortableTextWistiaVideo, { ...value });
  },
  inlineWistiaVideo: ({ value }: { value: InlineWistiaVideo }) => {
    return h(LazyInlineWistiaVideo, { ...value });
  },
  sectionBuilderWistiaVideo: ({ value }: { value: SectionBuilderWistiaVideo }) => {
    return h(LazySectionBuilderWistiaVideo, { ...value });
  },
  portableTextImage: ({ value }: { value: PortableTextImage }) => {
    return h(LazyPortableTextImageAtom, { ...value });
  },
  inlineImageAtom: ({ value }: { value: InlineImageAtom }) => {
    return h(LazyInlineImageAtom, { ...value });
  },
  sectionBuilderImage: ({ value }: { value: SectionBuilderImage }) => {
    return h(LazySectionBuilderImageAtom, { ...value });
  },
  insideLayoutPortableText: ({ value }: { value: InsideLayoutPortableText }) => {
    return h(LazyInsideLayout, { ...value });
  },
  portableTextMediaTwoColumn: ({ value }: { value: PortableTextMediaTwoColumn }) => {
    return h(LazyPortableTextMediaTwoColumn, { ...value });
  },
  sectionBuilderMediaTwoColumn: ({ value }: { value: SectionBuilderMediaTwoColumn }) => {
    return h(LazySectionBuilderMediaTwoColumn, { ...value });
  },
  portableTextContentGrid: ({ value }: { value: PortableTextContentGrid }) => {
    return h(LazyPortableTextContentGrid, { ...value });
  },
  sectionBuilderContentGrid: ({ value }: { value: SectionBuilderContentGrid }) => {
    return h(LazySectionBuilderContentGrid, { ...value });
  },
  portableTextFAQAccordion: ({ value }: { value: PortableTextFAQAccordion }) => {
    return h(LazyPortableTextFAQAccordion, { ...value });
  },
  sectionBuilderFAQAccordion: ({ value }: { value: SectionBuilderFAQAccordion }) => {
    return h(LazySectionBuilderFAQAccordion, { ...value });
  },
  portableTextFlipCards: ({ value }: { value: PortableTextFlipCards }) => {
    return h(LazyPortableTextFlipCards, { ...value });
  },
  sectionBuilderFlipCards: ({ value }: { value: SectionBuilderFlipCards }) => {
    return h(LazySectionBuilderFlipCards, { ...value });
  },
  sectionBuilderTestimonial: ({ value }: { value: SectionBuilderTestimonial }) => {
    return h(LazySectionBuilderTestimonial, { ...value });
  },
  portableTextTestimonial: ({ value }: { value: PortableTextTestimonial }) => {
    return h(LazyPortableTextTestimonial, { ...value });
  },
  sectionBuilderPortableText: ({ value }: { value: SectionBuilderPortableText }) => {
    return h(LazySectionBuilderPortableText, { ...value });
  },
  carouselCard: ({ value }: { value: CarouselCard }) => {
    return h(LazyCarouselCard, { ...value });
  },
  sectionBuilderLogoBand: ({ value }: { value: SectionBuilderLogoBand }) => {
    return h(LazySectionBuilderLogoBand, { ...value });
  },
  sectionBuilderJumboImageTwoColumn: ({ value }: { value: SectionBuilderJumboImageTwoColumn }) => {
    return h(LazySectionBuilderJumboImageTwoColumn, { ...value });
  },
  sectionBuilderSpinningTwoColumn: ({ value }: { value: SectionBuilderSpinningTwoColumn }) => {
    return h(LazySectionBuilderSpinningTwoColumn, { ...value });
  },
  sectionBuilderCalloutWithIcon: ({ value }: { value: SectionBuilderCalloutWithIcon }) => {
    return h(LazySectionBuilderCalloutWithIcon, { ...value });
  },
  portableTextCardTwoColumns: ({ value }: { value: PortableTextCardTwoColumns }) => {
    return h(LazyPortableTextCardTwoColumns, { ...value });
  },
  sectionBuilderCardTwoColumns: ({ value }: { value: SectionBuilderCardTwoColumns }) => {
    return h(LazySectionBuilderCardTwoColumns, { ...value });
  },
  portableTextContentCard: ({ value }: { value: PortableTextContentCard }) => {
    return h(LazyPortableTextContentCard, { ...value });
  },
  sectionBuilderContentCard: ({ value }: { value: SectionBuilderContentCard }) => {
    return h(LazySectionBuilderContentCard, { ...value });
  },
  portableTextSpeakerBio: ({ value }: { value: PortableTextSpeakerBio }) => {
    return h(LazyPortableTextSpeakerBio, { ...value });
  },
  sectionBuilderSpeakerBio: ({ value }: { value: SectionBuilderSpeakerBio }) => {
    return h(LazySectionBuilderSpeakerBio, { ...value });
  },
  sectionBuilderCarouselCard: ({ value }: { value: SectionBuilderCarouselCard }) => {
    return h(LazySectionBuilderCarouselCard, { ...value });
  },
  portableTextCarouselCard: ({ value }: { value: PortableTextCarouselCard }) => {
    return h(LazyPortableTextCarouselCard, { ...value });
  },
  sectionBuilderCycledTestimonial: ({ value }: { value: SectionBuilderCycledTestimonial }) => {
    return h(LazySectionBuilderCycledTestimonial, { ...value });
  },
  homePageInfographic: ({ value }: { value: HomePageInfographic }) => {
    return h(LazyHomePageInfographic, { ...value });
  },
  sectionBuilderDesignerCards: ({ value }: { value: SectionBuilderDesignerCards }) => {
    return h(LazySectionBuilderDesignerCards, { ...value });
  },
  callToAction: ({
    value,
    isInline,
  }: {
    value: { url: string; text: string };
    isInline: boolean;
  }) => {
    if (isInline) {
      return h('a', { href: value.url }, value.text);
    } else {
      return h('div', { class: 'callToAction' }, value.text);
    }
  },
  sectionBuilderWhatsNew: ({ value }: { value: SectionBuilderWhatsNew }) => {
    return h(LazySectionBuilderWhatsNew, { ...value });
  },
  codeEmbed: ({ value }: { value: CodeEmbed }) => {
    return h(LazyCodeEmbed, { ...value });
  },
  inlineCodeEmbed: ({ value }: { value: CodeEmbed }) => {
    return h(LazyInlineCodeEmbed, { ...value });
  },
  sectionBuilderComparisonPlanTable: ({ value }: { value: SectionBuilderComparisonPlanTable }) => {
    return h(LazySectionBuilderComparisonPlanTable, { ...value });
  },
  homepageHeader: ({ value }: { value: HomepageHeader }) => {
    return h(LazyHomepageHeader, { ...value });
  },
  codeEmbedPortableText: ({ value }: { value: CodeEmbedPortableText }) => {
    return h(LazyCodeEmbedPortableText, { ...value });
  },
  portableTextForm: ({ value }: { value: PortableTextForm }) => {
    return h(LazyPortableTextForm, { ...value });
  },
  personalizationVariable: ({ value }: { value: PersonalizationVariable }) => {
    return h(LazyPersonalizationVariable, { ...value });
  },
  ussSuccessRedirect: ({ value }: { value: UssSuccessRedirect }) => {
    return h(LazyUssSuccessRedirect, { ...value });
  },
  sectionBuilderForm: ({ value }: { value: SectionBuilderForm }) => {
    return h(LazySectionBuilderForm, { ...value });
  },
  sectionBuilderResourceCard: ({ value }: { value: SectionBuilderResourceCard }) => {
    return h(LazySectionBuilderResourceCard, { ...value });
  },
  sectionBuilderParallaxDeck: ({ value }: { value: SectionBuilderParallaxDeck }) => {
    return h(LazySectionBuilderParallaxDeck, { ...value });
  },
  sectionBuilderAnchorMenu: ({ value }: { value: SectionBuilderAnchorMenu }) => {
    return h(LazySectionBuilderAnchorMenu, { ...value });
  },
  sectionBuilderReviewCards: ({ value }: { value: SectionBuilderReviewCards }) => {
    return h(LazySectionBuilderReviewCards, { ...value });
  },
  latestNews: ({ value }: { value: LatestNews }) => {
    return h(LazyLatestNews, { ...value });
  },
  currencyDropDown: ({ value }: { value: CurrencyDropDown }) => {
    return h(LazyCurrencyDropdown, { ...value });
  },
  sectionBuilderCustomTable: ({ value }: { value: SectionBuilderCustomTable }) => {
    return h(LazySectionBuilderCustomTable, { ...value });
  },
  portableTextCustomTable: ({ value }: { value: PortableTextCustomTable }) => {
    return h(LazyPortableTextCustomTable, { ...value });
  },
  marketingBanners: ({ value }: { value: MarketingBanners }) => {
    return h(LazyMarketingBanners, { ...value });
  },
  marketingBanner: ({ value }: { value: MarketingBanner }) => {
    return h(LazyMarketingBanner, { ...value });
  },
  breakoutSection: ({ value }: { value: BreakoutSection }) => {
    return h(LazyBreakoutSection, { ...value });
  },
  horizontalLine: ({ value }: { value: HorizontalLine }) => {
    return h(LazyHorizontalLine, { ...value });
  },
  closableBanner: ({ value }: { value: ClosableBanner }) => {
    return h(LazyClosableBanner, { ...value });
  },
  sectionBuilderScrollingLogoBand: ({ value }: { value: SectionBuilderScrollingLogoBand }) => {
    return h(LazySectionBuilderScrollingLogoBand, { ...value });
  },
  portableTextCalloutWithIcon: ({ value }: { value: PortableTextCalloutWithIcon }) => {
    return h(LazyPortableTextCalloutWithIcon, { ...value });
  },
  portableTextCalloutBanner: ({ value }: { value: PortableTextCalloutBanner }) => {
    return h(LazyPortableTextCalloutBanner, { ...value });
  },
  aiAdvisor: ({ value }: { value: AiAdvisor }) => {
    return h(LazyAiAdvisor, { ...value });
  },
} as Record<MappedComponent, () => VNode>;
export const getComponent = (key: MappedComponent) => {
  if (!_mappedComponentKeys.includes(key)) {
    console.warn(`Component ${key} not found in component map`);
  }
  return componentMap[key];
};
